import { createContext, startTransition, useCallback, useEffect, useMemo, useRef } from "react";
import io from 'socket.io-client';
import useAppState, { useNetworkState } from "../hooks/appStateHook";
import useInternet from "../hooks/connectivityHook";
import PropTypes from 'prop-types';
import { useDispatch } from "react-redux";
import useNetwork from "../hooks/networkHook";
import { useNavigate } from "react-router-dom";
import useCredentials from "../hooks/credentialsHook";
class FixedSizeSet {
    constructor(limit = 150) {
        this.limit = limit;
        this.map = new Map(); // maintains insertion order
    }

    insert(value) {
        if (this.map.has(value)) {
            // Remove to re-insert and update order
            this.map.delete(value);
        }
        this.map.set(value, true); // or store metadata if needed

        if (this.map.size > this.limit) {
            // Remove the oldest (first inserted)
            const oldestKey = this.map.keys().next().value;
            this.map.delete(oldestKey);
        }
    }

    has(value) {
        return this.map.has(value);
    }

    values() {
        return Array.from(this.map.keys());
    }
}

class BatchDispatcher {
    constructor() {
        this.buffer = new Map();
    }

    /**
     * Add an action to the batch.
     * @param {{ type: string, payload: any }} action - A standard Redux action object
     */
    add(action) {
        const { type, payload } = action;
        if (!type) {
            throw new Error('Action must have a type');
        }

        if (!this.buffer.has(type)) {
            this.buffer.set(type, []);
        }

        const payloads = this.buffer.get(type);

        if (Array.isArray(payload)) {
            payloads.push(...payload);
        } else {
            payloads.push(payload);
        }
    }

    /**
     * Dispatch all batched actions and clear the buffer.
     * @param {function} dispatch - Redux store's dispatch function
     */
    process(dispatch) {
        if (this.buffer.size > 0) {
            for (const [type, payloads] of this.buffer.entries()) {
                if (payloads.length > 0) {
                    dispatch({ type, payload: payloads });
                }
            }
            this.buffer.clear();
        }
    }

    /**
     * Check if there are any pending actions.
     * @returns {boolean}
     */
    hasPending() {
        for (const arr of this.buffer.values()) {
            if (arr.length > 0) return true;
        }
        return false;
    }
}


export const WebsocketContext = createContext({});

const WebsocketDirectProvider = ({ interval = 1200, children }) => {
    const { isOnline, wsStatus, setWSStatus } = useNetworkState();
    const { token } = useAppState();
    const credentials = useCredentials();
    const dispatch = useDispatch();
    const network = useNetwork();
    useInternet();
    const socket = useRef();
    const eventRegistry = useRef(new Map());
    const batchDispatcher = useRef(new BatchDispatcher());
    const lastInfo = useRef(new FixedSizeSet());
    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            if (socket.current) {
                socket.current.off();
                socket.current.disconnect();
                socket.current = null;
            }
        }
    }, []);

    useEffect(() => {
        const intervalID = setInterval(() => {
            batchDispatcher.current.process(dispatch)
        }, interval);
        return () => clearInterval(intervalID);
    }, [dispatch, interval])

    const emit = useCallback((event, data) => {
        if (socket.current) {
            socket.current.emit(event, data);
            return true;
        }
        return false;
    }, []);

    const isRegistered = useCallback((event, callback) => {
        if (eventRegistry.current.has(event)) {
            return eventRegistry.current.get(event).has(callback);
        }
        return false;
    }, []);
    
    const processEvent = useCallback((event) => {
        return function (data, metadata) {
            if (!metadata?.uuid || !lastInfo.current.has(metadata.uuid)) {
                if (metadata?.uuid) {
                    lastInfo.current.insert(metadata.uuid);
                }
                if (eventRegistry.current.has(event)) {
                    for (const [callback, count] of eventRegistry.current.get(event)) {
                        if (count >  0) {
                            callback(data, { dispatch: action => batchDispatcher.current.add(action), network, credentials, websocket: { emit, isRegistered }, metadata })
                        }
                    }
                }
            }
        }
    }, [network, credentials, emit, isRegistered]);

    useEffect(() => {
        if (socket.current) {
            for (const [event] of eventRegistry.current) {
                if (event !== 'reconnected' && event !== 'disconnected') {
                    socket.current.off(event);
                    socket.current.on(event, processEvent(event));
                }
            }
        }
    }, [processEvent])

    const onAuthenticated = useCallback(() => {
        setWSStatus('authenticated')
        if (eventRegistry.current.has('reconnected')) {
            for (const [callback, count] of eventRegistry.current.get('reconnected')) {
                if (count > 0) {
                    callback();
                }
            }
        }
    }, [setWSStatus]);

    useEffect(() => {
        if (socket.current) {
            socket.current.on('authenticated', onAuthenticated);
        }
    }, [onAuthenticated]);

    const onReconnect = useCallback(() => {
        setWSStatus('connecting')
        if (token) {
            socket.current.emit('authenticate', { token });
        }
    }, [token, setWSStatus]);

    useEffect(() => {
        if (socket.current) {
            socket.current.on('reconnect', onReconnect);
        }
    }, [onReconnect])

    const onOpen = useCallback(() => {
        for (const [event] of eventRegistry.current) {
            socket.current.on(event, processEvent(event))
        }
        onReconnect();
    }, [onReconnect, processEvent]);

    useEffect(() => {
        if (socket.current) {
            socket.current.on('connect', onOpen);
        }
    }, [onOpen])

    const onClose = useCallback(() => {
        if (wsStatus !== 'closed') {
            setWSStatus('closed');
            if (eventRegistry.current.has('disconnected')) {
                for (const [callback, count] of eventRegistry.current.get('disconnected')) {
                    if (count > 0) {
                        callback()
                    }
                }
            }
        }
    }, [wsStatus, setWSStatus]);

    useEffect(() => {
        if (socket.current) {
            socket.current.on('connect_error', onClose);
            socket.current.on('disconnect', onClose);
        }
    }, [onClose]);


    useEffect(() => {
        if (!socket.current && isOnline && token && wsStatus === 'closed') {
            socket.current = io(window.APP_HOST, {
                path: '/socket.io',
                autoConnect: false
            });
            setWSStatus('connecting');
            socket.current.off();
            socket.current.on('connect', onOpen);
            socket.current.on('connect_error', onClose);
            socket.current.on('disconnect', onClose);
            socket.current.on('reconnect', onReconnect);
            socket.current.on('authenticated', onAuthenticated);
            socket.current.on('force_reload', () => window.location.reload(true));
            socket.current.on('force_logout', () => startTransition(() => navigate('/logout')));
            socket.current.connect();
        } else if (socket.current && (!token || !isOnline) && wsStatus !== 'closed') {
            socket.current.close();
            socket.current = null;
            onClose();
        }
    }, [isOnline, token, wsStatus, setWSStatus, onOpen, onClose, onReconnect, onAuthenticated, navigate]);


    const unregister = useCallback((event, callback) => {
        if (eventRegistry.current.has(event)) {
            const callbackRegistry = eventRegistry.current.get(event);
            if (callbackRegistry.has(callback)) {
                const count = callbackRegistry.get(callback);
                if (count === 1) {
                    callbackRegistry.delete(callback);
                    if (callbackRegistry.size === 0) {
                        eventRegistry.current.delete(event);
                    }
                } else {
                    callbackRegistry.set(callback, count - 1);
                }
            }
        }
    }, []);

    const register = useCallback((event, callback) => {
        if (eventRegistry.current.has(event)) {
            const callbackRegistry = eventRegistry.current.get(event);
            if (callbackRegistry.has(callback)) {
                callbackRegistry.set(callback, callbackRegistry.get(callback) + 1);
            } else {
                callbackRegistry.set(callback, 1);
            }
        } else {
            eventRegistry.current.set(event, new Map([[ callback, 1 ]]));
            if (socket.current && event !== 'reconnected' && event !== 'disconnected') {
                socket.current.on(event, processEvent(event));
            }
        }
        return () => unregister(event, callback);
    }, [processEvent, unregister]);

    const value = useMemo(() => ({ register, unregister, isRegistered, emit }), [register, unregister, isRegistered, emit]);

    return <WebsocketContext.Provider value={value}>
        {children}
    </WebsocketContext.Provider>
}
WebsocketDirectProvider.propTypes = {
    children: PropTypes.node
}

const sendToRN = (type, data) => {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, data }));
}

export const WebsocketRNProvider = ({ interval = 1200, children }) => {
    const { isOnline, wsStatus, setWSStatus } = useNetworkState();
    const { token } = useAppState();
    const credentials = useCredentials();
    const dispatch = useDispatch();
    const network = useNetwork();
    useInternet();
    const eventRegistry = useRef(new Map());
    const batchDispatcher = useRef(new BatchDispatcher());
    const lastInfo = useRef(new FixedSizeSet());

    useEffect(() => {
        return () => {
            sendToRN('SOCKET_OFF');
            sendToRN('SOCKET_DISCONNECT');
        }
    }, []);

    useEffect(() => {
        const intervalID = setInterval(() => {
            batchDispatcher.current.process(dispatch)
        }, interval);
        return () => clearInterval(intervalID);
    }, [dispatch, interval])

    const emit = useCallback((event, data) => {
        sendToRN('SOCKET_EMIT', { event, data });
        return true;
    }, []);

    const isRegistered = useCallback((event, callback) => {
        if (eventRegistry.current.has(event)) {
            return eventRegistry.current.get(event).has(callback);
        }
        return false;
    },[]);

    const processEvent = useCallback(({ event, data, metadata }) => {
        const key = metadata?.uuid ? `${metadata.uuid}_${event}` : null;
        if (!key || !lastInfo.current.has(key)) {
            if (key) {
                lastInfo.current.insert(key);
            }
            if (eventRegistry.current.has(event)) {
                for (const [callback, count] of eventRegistry.current.get(event)) {
                    if (count >  0) {
                        callback(data, { dispatch: action => batchDispatcher.current.add(action), network, credentials, websocket: { emit, isRegistered }, metadata })
                    }
                }
            }
        }
    }, [network, credentials, emit, isRegistered]);

    useEffect(() => {
        const handler = (event) => {
            const data = event.data;
            if (!event.type || data.type !== 'SOCKET_EVENT') {
                return;
            }

            switch (data.event) {
                case 'authenticated': {
                    setWSStatus('authenticated');
                    if (eventRegistry.current.has('reconnected')) {
                        for (const [callback, count] of eventRegistry.current.get('reconnected')) {
                            if (count > 0) {
                                callback();
                            }
                        }
                    }
                    break;
                }
                case 'open':
                case 'reconnect': {
                    setWSStatus('connecting');
                    if (token) {
                        sendToRN('SOCKET_EMIT', { event: 'authenticate', data: { token }});
                    }
                    break;
                }
                case 'close': {
                    if (wsStatus !== 'closed') {
                        setWSStatus('closed');
                        if (eventRegistry.current.has('disconnected')) {
                            for (const [callback, count] of eventRegistry.current.get('disconnected')) {
                                if (count > 0) {
                                    callback()
                                }
                            }
                        }
                    }
                    break;
                }
                case 'connect_error':
                case 'disconnect': {
                    if (wsStatus !== 'closed') {
                        setWSStatus('closed');
                        if (eventRegistry.current.has('disconnected')) {
                            for (const [callback, count] of eventRegistry.current.get('disconnected')) {
                                if (count > 0) {
                                    callback()
                                }
                            }
                        }
                    }
                    break;
                }
                case 'force_reload':
                    window.location.reload(true);
                    break;
                default:
                    processEvent(data);
                    break;
            }
        }
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [token, wsStatus, setWSStatus, processEvent]);

    useEffect(() => {
        if (isOnline && token && wsStatus === 'closed') {
            sendToRN('SOCKET_INIT');
        } else if ((!token || !isOnline) && wsStatus !== 'closed') {
            sendToRN('SOCKET_CLOSE');
        }
    }, [isOnline, token, wsStatus, setWSStatus]);

    const unregister = useCallback((event, callback) => {
        if (eventRegistry.current.has(event)) {
            const callbackRegistry = eventRegistry.current.get(event);
            if (callbackRegistry.has(callback)) {
                const count = callbackRegistry.get(callback);
                if (count === 1) {
                    callbackRegistry.delete(callback);
                    if (callbackRegistry.size === 0) {
                        eventRegistry.current.delete(event);
                    }
                } else {
                    callbackRegistry.set(callback, count - 1);
                }
            }
        }
    }, []);

    const register = useCallback((event, callback) => {
        if (eventRegistry.current.has(event)) {
            const callbackRegistry = eventRegistry.current.get(event);
            if (callbackRegistry.has(callback)) {
                callbackRegistry.set(callback, callbackRegistry.get(callback) + 1);
            } else {
                callbackRegistry.set(callback, 1);
            }
        } else {
            eventRegistry.current.set(event, new Map([[ callback, 1 ]]));
        }
        return () => unregister(event, callback);
    }, [unregister]);

    const value = useMemo(() => ({ register, unregister, isRegistered, emit }), [register, unregister, isRegistered, emit]);

    return <WebsocketContext.Provider value={value}>
        {children}
    </WebsocketContext.Provider>
}
WebsocketRNProvider.propTypes = {
    children: PropTypes.node
}

export const WebsocketProvider = ({ children }) => {
    if (window.ReactNativeWebView && window.APP_VERSION > 1) {
        return <WebsocketRNProvider>{children}</WebsocketRNProvider>
    }
    return <WebsocketDirectProvider>{children}</WebsocketDirectProvider>
}